import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * VaultSummary (W-2 Home): protected / not covered counts and the insured
 * value, as a labelled region with a description list. Values arrive
 * already formatted (`@kovernow/flows/format`), labels already translated.
 */

export interface VaultSummaryStat {
  id: string;
  label: string;
  value: string;
  /** Optional destination; the value is rendered as a link (W-3). */
  href?: string;
}

export interface VaultSummaryLinkProps {
  href: string;
  className: string;
  /** "{value} {label}": the bare number alone doesn't say where the link goes. */
  "aria-label": string;
  children: React.ReactNode;
}

export interface VaultSummaryProps extends React.ComponentProps<"section"> {
  title: string;
  stats: VaultSummaryStat[];
  /** Optional link (e.g. "Open vault"). */
  action?: React.ReactNode;
  headingLevel?: 2 | 3;
  /** Renders a stat link (e.g. a router Link); defaults to `<a>`. */
  renderLink?: (props: VaultSummaryLinkProps) => React.ReactNode;
}

const defaultLink = ({ href, className, "aria-label": ariaLabel, children }: VaultSummaryLinkProps) => (
  <a href={href} className={className} aria-label={ariaLabel}>
    {children}
  </a>
);

function VaultSummary({
  title,
  stats,
  action,
  headingLevel = 2,
  renderLink = defaultLink,
  className,
  ...props
}: VaultSummaryProps) {
  const id = React.useId();
  const Heading = `h${headingLevel}` as "h2";
  return (
    <section
      data-slot="vault-summary"
      aria-labelledby={`${id}-title`}
      className={cn("rounded-lg border bg-card p-4 text-card-foreground", className)}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <Heading id={`${id}-title`} className="text-lg font-semibold">
          {title}
        </Heading>
        {action}
      </div>
      <dl
        className={cn(
          "mt-3 grid grid-cols-1 gap-3",
          stats.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3",
        )}
      >
        {stats.map((s) => (
          <div key={s.id} data-stat={s.id} className="flex flex-col">
            <dt className="text-sm text-muted-foreground">{s.label}</dt>
            <dd className="text-xl font-semibold">
              {s.href
                ? renderLink({
                    href: s.href,
                    "aria-label": `${s.value} ${s.label}`,
                    className: "underline underline-offset-4 hover:no-underline focus-visible:outline-2 focus-visible:outline-ring",
                    children: s.value,
                  })
                : s.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export { VaultSummary };
